/**
 * Debug Utility
 * Provides controlled console logging throughout the application
 * Usage: import { debugLog } from '@/utils/debug';
 */

import { debug as debugConfig } from './env';

type DebugArea = 'app' | 'luna' | 'video';

/**
 * Conditional console.log that respects REACT_APP_DEBUG_MODE
 */
export const debugLog = (area: DebugArea, ...args: unknown[]): void => {
  if (debugConfig.isEnabled(area)) {
    console.log(...args);
  }
};

/**
 * Conditional console.info that respects REACT_APP_DEBUG_MODE
 */
export const debugInfo = (area: DebugArea, ...args: unknown[]): void => {
  if (debugConfig.isEnabled(area)) {
    console.info(...args);
  }
};

/**
 * Conditional console.debug that respects REACT_APP_DEBUG_MODE
 */
export const debugDebug = (area: DebugArea, ...args: unknown[]): void => {
  if (debugConfig.isEnabled(area)) {
    console.debug(...args);
  }
};

/**
 * Always log warnings (not affected by debug mode)
 */
export const debugWarn = (...args: unknown[]): void => {
  console.warn(...args);
};

/**
 * Always log errors (not affected by debug mode)
 */
export const debugError = (...args: unknown[]): void => {
  console.error(...args);
};

/**
 * Log initialization messages (app area)
 */
export const logInit = (...args: unknown[]): void => {
  debugLog('app', ...args);
};

/**
 * Log Luna AI messages
 */
export const logLuna = (...args: unknown[]): void => {
  debugLog('luna', ...args);
};

/**
 * Log video-related messages
 */
export const logVideo = (...args: unknown[]): void => {
  debugLog('video', ...args);
};

/**
 * Check if debug mode is enabled
 */
export const isDebugEnabled = (area?: DebugArea): boolean => {
  return debugConfig.isEnabled(area);
};
